import { useMemo, useState } from 'react'
import { Alert, Button, Card, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import {
  applyLocalList,
  byInstant,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { LeaveTypeFormModal } from '@/features/hr/LeaveTypeFormModal'
import { fetchLeaveTypes, type LeaveType } from '@/features/hr/leaveApi'

/**
 * Ta'til turlari katalogi.
 *
 * <h2>⚠️ KATALOG BO'SH TUG'ILADI va bu ATAYLAB</h2>
 * Migratsiya birorta tur ekmaydi (`xsib-hr/README.md`): `annual_days`
 * QONUNIY huquq va uni o'ylab topish balansni JIMGINA noto'g'ri
 * qilardi. Bo'sh katalog esa KO'RINADIGAN nosozlik — birinchi ta'til
 * so'rovi 404 oladi va operator shu yerga keladi.
 *
 * Shuning uchun bo'sh holat ayblamaydi, yo'l ko'rsatadi.
 *
 * <h2>⛔ SAHIFALASH YO'Q — SERVER UNI QO'LLAB-QUVVATLAMAYDI</h2>
 * `LeaveTypeController#list` yagona parametr oladi — `activeOnly`.
 * `page`/`size`/`sort`/`q` YO'Q. Soxta sahifalash yasalmadi (egasi
 * qarori, 2026-08-20): butun ro'yxatni olib mijozda kesish sahifalash
 * borday ko'rsatardi. Qidiruv va saralash esa TO'LIQ massiv ustida
 * ishlaydi, ya'ni «faqat joriy sahifa saralandi» degan yolg'on tartib
 * bu yerda paydo bo'lmaydi (`shared/lib/localList.ts`).
 *
 * <h2>⚠️ O'CHIRISH TUGMASI YO'Q</h2>
 * Backendda `DELETE` yo'q: turi o'chirilgan so'rovlar nomsiz qolardi.
 * O'rniga «faol» bayrog'i bor.
 *
 * <h2>⚠️ SUKUT BO'YICHA FAQAT FAOL TURLAR (egasi qarori 2026-08-20)</h2>
 * «Yopilganlar qoidasi»: nofaol tur ro'yxatda ko'rinmaydi va u HOLAT
 * FILTRI orqali olinadi. Filtrning bo'sh holati yo'q — u `active` dan
 * boshlanadi. «Faol» serverga (`activeOnly`) uzatiladi, «nofaol» esa
 * serverda umuman yo'q va TO'LIQ ro'yxat ustida hisoblanadi.
 */
export function LeaveTypesPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canWrite = can('hr.leavetype.write')

  const [editing, setEditing] = useState<LeaveType | null>(null)
  const [open, setOpen] = useState(false)

  // «Oxirgi qo'shilgan birinchi» — `LeaveTypeView.createdAt` javobda bor
  const list = useLocalListState({ defaultSort: { field: 'createdAt', order: 'descend' } })
  // ⚠️ Filtr qo'yilmagan holat = `active`, `null` EMAS (sinf javadoc'i).
  const activeFilter = list.filter('active') ?? 'active'
  // ⚠️ «Faol» SERVERGA boradi, «nofaol» serverda umuman yo'q va to'liq
  //    ro'yxat ustida hisoblanadi.
  const activeOnly = activeFilter === 'active'

  const types = useQuery({
    queryKey: queryKeys.hr.leaveTypes(activeOnly),
    queryFn: () => fetchLeaveTypes(activeOnly),
  })

  const rows = useMemo(() => types.data ?? [], [types.data])

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: list.search,
        searchFields: (row) => [row.code, row.name],
        filters: {
          active: (row, value) => (value === 'inactive' ? !row.active : true),
          paid: (row, value) => row.paid === (value === 'yes'),
        },
        filterState: list.state.filters,
        comparators: {
          code: byText((row) => row.code),
          name: byText((row) => row.name),
          createdAt: byInstant((row) => row.createdAt),
        },
        sort: list.sort,
      }),
    [rows, list.search, list.state.filters, list.sort],
  )

  const columns: ColumnsType<LeaveType> = [
    {
      // Katalog sahifalanmaydi — raqam to'g'ridan-to'g'ri `index` dan.
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    {
      title: t('hr.leaveType.code'),
      dataIndex: 'code',
      key: 'code',
      ...sortColumn(list.sort, 'code'),
    },
    {
      title: t('hr.leaveType.name'),
      dataIndex: 'name',
      key: 'name',
      ...sortColumn(list.sort, 'name'),
    },
    {
      title: t('hr.leaveType.annualDays'),
      key: 'annualDays',
      align: 'right',
      // ⚠️ `null` = cheklovsiz. `0` deb ko'rsatish teskari ma'no berardi.
      render: (_value, row) =>
        row.annualDays === null ? t('hr.leave.unlimited') : row.annualDays,
    },
    {
      title: t('hr.leaveType.usesBalance'),
      key: 'usesBalance',
      render: (_value, row) =>
        row.usesBalance ? <Tag color="blue">{t('common.yes')}</Tag> : <Tag>{t('common.no')}</Tag>,
    },
    {
      title: t('hr.leaveType.paid'),
      key: 'paid',
      render: (_value, row) =>
        row.paid ? <Tag color="green">{t('common.yes')}</Tag> : <Tag>{t('common.no')}</Tag>,
    },
    {
      title: t('hr.leaveType.requiresApproval'),
      key: 'requiresApproval',
      render: (_value, row) =>
        row.requiresApproval ? (
          <Tag>{t('common.yes')}</Tag>
        ) : (
          <Tag color="gold">{t('hr.leaveType.autoApproved')}</Tag>
        ),
    },
    {
      title: t('hr.leaveType.active'),
      key: 'active',
      render: (_value, row) =>
        row.active ? (
          <Tag color="green">{t('common.yes')}</Tag>
        ) : (
          <Tag color="default">{t('common.no')}</Tag>
        ),
    },
    {
      title: t('common.createdAt'),
      key: 'createdAt',
      width: 180,
      ...sortColumn(list.sort, 'createdAt'),
      // Standart tartib shu ustun bo'yicha — ko'rinmasa qatorlarning
      // joylashuvi tushuntirilmagan bo'lardi.
      render: (_value, row) =>
        row.createdAt === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
        ),
    },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'actions',
            width: 56,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_value: unknown, row: LeaveType) => (
              <RowActions
                items={[
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    onClick: () => {
                      setEditing(row)
                      setOpen(true)
                    },
                    permission: 'hr.leavetype.write',
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <PageHeader
        title={t('nav.hrLeaveTypes')}
        description={t('pageIntro.hrLeaveTypes')}
        extra={
          <Can permission="hr.leavetype.write">
            <Button
              type="primary"
              onClick={() => {
                setEditing(null)
                setOpen(true)
              }}
            >
              {t('hr.leaveType.createAction')}
            </Button>
          </Can>
        }
      />

      <Card>
        {types.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(types.error, t)}
          />
        ) : null}

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <ExpandableHint text={t('hr.leaveType.listHint')} fontSize={13} />

          <Flex wrap gap="small" align="center">
            <Input.Search
              allowClear
              value={list.searchText}
              onChange={(e) => list.setSearchText(e.target.value)}
              placeholder={t('hr.leaveType.searchPlaceholder')}
              style={{ width: 260, maxWidth: '100%' }}
            />
            <Select<string>
              allowClear
              value={list.filter('paid')}
              onChange={(value) => list.setFilter('paid', value ?? null)}
              placeholder={t('hr.leaveType.paid')}
              style={{ width: 160 }}
              options={[
                { value: 'yes', label: t('common.yes') },
                { value: 'no', label: t('common.no') },
              ]}
            />
            {/* ⚠️ `allowClear` YO'Q: bo'sh holat «hammasi» degan ma'no
                berardi, aslida esa sukut — faqat faollar. */}
            <Select<string>
              value={activeFilter}
              onChange={(value) => list.setFilter('active', value)}
              aria-label={t('listView.statusFilter')}
              style={{ width: 170 }}
              options={[
                { value: 'active', label: t('listView.active') },
                { value: 'inactive', label: t('listView.inactive') },
              ]}
            />
            {list.narrowed ? (
              <>
                <Typography.Text type="secondary">
                  {t('listView.shown', { shown: visible.length, total: rows.length })}
                </Typography.Text>
                <Button type="link" size="small" onClick={list.reset}>
                  {t('dataTable.clearFilters')}
                </Button>
              </>
            ) : null}
          </Flex>

          {/* ⚠️ Bo'sh xulosa FAQAT `isSuccess` da (`guards.mjs` 3-bo'limi).
              ⚠️ «Katalog bo'sh» va «filtr hech narsa topmadi» ARALASHMAYDI:
                 birinchisi shu yerda, ikkinchisi jadval ichida — u yerda
                 filtrni tozalash tugmasi bilan. */}
          {types.isSuccess && rows.length === 0 ? (
            activeOnly ? (
              // ⚠️ Server FAOLLARNI so'radi va bittasini ham topmadi.
              //    Bu «katalog bo'sh tug'iladi» holati BILAN BIR XIL
              //    EMAS: nofaol turlar turgan bo'lishi mumkin va ular
              //    bir bosishda ochiladi. Ikkovini bir xil matn bilan
              //    ko'rsatish operatorni mavjud turni qaytadan
              //    yaratishga yuborardi.
              <EmptyState
                title={t('listView.activeOnlyEmpty')}
                description={t('listView.activeOnlyEmptyHint')}
                actionLabel={t('listView.activeOnlyEmptyAction')}
                onAction={() => list.setFilter('active', 'inactive')}
              />
            ) : (
              <EmptyState
                title={t('hr.leaveType.empty')}
                description={t('hr.leaveType.emptyHint')}
                {...(canWrite
                  ? {
                      actionLabel: t('hr.leaveType.createAction'),
                      onAction: () => {
                        setEditing(null)
                        setOpen(true)
                      },
                    }
                  : {})}
              />
            )
          ) : (
            <Table<LeaveType>
              rowKey={(row) => row.id}
              columns={columns}
              dataSource={[...visible]}
              loading={types.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 1180 }}
              onChange={localSortChange<LeaveType>(list.patch)}
              locale={{
                emptyText: (
                  <EmptyState
                    variant="no-data"
                    title={t('listView.noMatch')}
                    description={t('listView.noMatchHint', { total: rows.length })}
                    actionLabel={t('dataTable.clearFilters')}
                    onAction={list.reset}
                  />
                ),
              }}
            />
          )}

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('listView.fullListNote')}
          </Typography.Text>
        </Space>
      </Card>

      <LeaveTypeFormModal open={open} type={editing} onClose={() => setOpen(false)} />
    </>
  )
}
